import { useState, useEffect, useRef, useMemo } from "react"
import SelectField from '../common/SelectField.js';
import { useNavigate } from "react-router-dom"
import { mlSheetService } from "../../services/mlSheetService"
import vehicleService from '../../services/vehicleService.js'
import routeService from '../../services/RouteService.js'
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader"
import Alert from "../common/Alert"
import { ArrowLeft, Camera, X, Search, ChevronDown, Upload, File, Trash2, Eye, Check, Plus } from "lucide-react"
import Button from '../common/Button.js';
import CreateVehicleWithOwnerModal from './CreateVehicleWithOwnerModal.js';
import { mergeCanAccess } from '../../utils/companyFeatures.js';
import { isMlAccessUsersRequired, hasShipmentCrossingAccess } from '../../utils/networkMode.js';
import vehicleOwnerService from '../../services/VehicleOwnerService.js';
import { enrichVehiclesWithOwners, formatVehicleLabel } from '../../utils/vehicleOwnerDisplay.js';
import { groupAccessAgents, formatAccessAgentMeta, matchingAccessAgentIds } from '../../utils/mlSheetAccessAgentList.js';
import { prepareScanImageFile, isAcceptedScanFile, isAcceptedImageFile } from '../../utils/normalizeCameraImage.js';
import {
  formatLocationDropdownLabel,
  filterLocationsByQuery,
} from '../../utils/locationDropdownLabel.js';
import { isLrShipmentFormat } from '../../utils/shipmentIdFormat.js';
import companySettingsService from '../../services/companySettingsService.js';
import { formatRouteLabel } from '../../utils/routeDisplay.js';
import { showMlSheetAutoSyncAlerts } from '../../utils/mlSheetAutoSyncAlerts.js';
import LiveQrScanner, { supportsLiveQrScan } from '../common/LiveQrScanner.js';
import { useTranslation } from 'react-i18next';
import useMlCreationPaidSettlement from '../../hooks/useMlCreationPaidSettlement.js';
import MlPaidSettlementGate from './MlPaidSettlementGate.js';
import { ensureCameraPermission } from '../../utils/devicePermissions.js';
import MlAssignToLocationBlock from './MlAssignToLocationBlock.js';
import { hubIdOf, ownHubsEligibleForMlLocationAssign } from '../../utils/mlAssignToLocation.js';


const CreateMLSheetQR = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { user } = useAuth()
  const canAddVehicle = (user?.role || user?.role_name) === 'Admin'
  const fileInputRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [agents, setAgents] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [locations, setLocations] = useState([])
  const [useLrLocationLabels, setUseLrLocationLabels] = useState(false)
  const [routes, setRoutes] = useState([])
  const [routeLocked, setRouteLocked] = useState(false)
  
  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [processingFile, setProcessingFile] = useState(false)
  
  // Extracted shipments from documents
  const [extractedShipments, setExtractedShipments] = useState([])
  const [selectedShipmentIds, setSelectedShipmentIds] = useState([])
  const {
    mlCreationBlock,
    dues,
    agentDues,
    isHubAdmin,
    hardBlocked: mlHardBlocked,
    showGate: showPaidSettlementGate,
    settlementPath,
    settleOpen,
    setSettleOpen,
    refresh: refreshMlSettlement,
  } = useMlCreationPaidSettlement(user)
  
  // Form state
  const [formData, setFormData] = useState({
    vehicle_id: "",
    driver_contact: "",
    access_user_ids: [],
    crossing_location_hub_ids: [],
    route_id: "",
  })
  const [assignToLocation, setAssignToLocation] = useState(false)
  const [assignedLocationHubId, setAssignedLocationHubId] = useState("")
  
  // Extracted data from documents
  const [extractedData, setExtractedData] = useState({
    fromLocationIds: user?.location_hub_id && ['Agent', 'Hub Admin'].includes(user?.role || user?.role_name)
      ? [user.location_hub_id]
      : [],
    toLocationIds: [],
    shipmentIds: [],
  })

  const [searchAgentInput, setSearchAgentInput] = useState("")
  const [showAgentDropdown, setShowAgentDropdown] = useState(false)
  const [previewImage, setPreviewImage] = useState(null)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [searchInputs, setSearchInputs] = useState({
    vehicle: "",
    access: "",
    crossing: "",
    route: "",
  })
  const [existingMLS, setExistingMLS] = useState([])
  const [existingLoading, setExistingLoading] = useState(false)
  const [existingError, setExistingError] = useState("")
  const [actionMode, setActionMode] = useState("create")
  const [selectedExistingSheet, setSelectedExistingSheet] = useState("")
  const [addExistingLoading, setAddExistingLoading] = useState(false)

  const [showVehicleModal, setShowVehicleModal] = useState(false)
  const [pendingVehicleSearch, setPendingVehicleSearch] = useState('')
  const [liveScannerOpen, setLiveScannerOpen] = useState(false)
  const [liveScanning, setLiveScanning] = useState(false)

  const companyAccess = mergeCanAccess(user?.company_features || user?.company_can_access || {})
  const accessUsersRequired = isMlAccessUsersRequired(user)
  const canSelectCrossingHubs = hasShipmentCrossingAccess(user)
  const fromLocationLocked = ['Agent', 'Hub Admin'].includes(user?.role || user?.role_name)
  const lockedFromId = user?.location_hub_id

  const applyLockedFrom = (fromIds) => {
    if (!fromLocationLocked || lockedFromId == null) return fromIds
    return [lockedFromId]
  }

  useEffect(() => {
    if (!fromLocationLocked || lockedFromId == null) return
    setExtractedData((prev) => {
      if (prev.fromLocationIds.length === 1 && String(prev.fromLocationIds[0]) === String(lockedFromId)) {
        return prev
      }
      return { ...prev, fromLocationIds: [lockedFromId] }
    })
  }, [fromLocationLocked, lockedFromId])

  const vehicleRef = useRef(null)
  const accessRef = useRef(null)
  const crossingRef = useRef(null)
  const routeRef = useRef(null)
  const cameraInputRef = useRef(null)

  useEffect(() => {
    fetchInitialData()
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !vehicleRef.current?.contains(e.target) &&
        !accessRef.current?.contains(e.target) &&
        !crossingRef.current?.contains(e.target) &&
        !routeRef.current?.contains(e.target)
      ) {
        setOpenDropdown(null)
        setSearchInputs({
          vehicle: "",
          access: "",
          crossing: "",
          route: "",
        })
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (routeLocked) return
    const from = extractedData.fromLocationIds
    const to = extractedData.toLocationIds
    if (!from?.length || !to?.length) {
      setFormData((prev) => (prev.route_id ? { ...prev, route_id: "" } : prev))
      return
    }
    let cancelled = false
    routeService
      .match(from, to)
      .then((res) => {
        if (cancelled) return
        const matched = res?.data
        setFormData((prev) => ({ ...prev, route_id: matched?.id || "" }))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [extractedData.fromLocationIds, extractedData.toLocationIds, routeLocked])

  const fetchInitialData = async () => {
    try {
      setLoading(true)
      
      // Fetch agents/access users
      const agentsResponse = await mlSheetService.getAgents()
      setAgents(Array.isArray(agentsResponse.data) ? agentsResponse.data : agentsResponse.data.data || [])
      
      // Fetch vehicles and owners
      const [vehiclesResponse, ownersRes, routesRes] = await Promise.all([
        vehicleService.getAllVehicles(),
        vehicleOwnerService.getAll(1, 500).catch(() => ({ data: [] })),
        routeService.getAll(1, 1000).catch(() => ({ data: [] })),
      ])
      const vehiclesRaw = Array.isArray(vehiclesResponse.data)
        ? vehiclesResponse.data
        : vehiclesResponse.data.data || []
      const ownersData = ownersRes?.data || ownersRes || []
      setVehicles(
        enrichVehiclesWithOwners(
          vehiclesRaw,
          Array.isArray(ownersData) ? ownersData : []
        )
      )
      
      // Fetch locations
      const [locationsResponse, settingsRes] = await Promise.all([
        mlSheetService.getLocations(),
        companySettingsService.getSettings().catch(() => ({ data: {} })),
      ])
      const locationsData = locationsResponse.data.hubs || locationsResponse.data.data || locationsResponse.data || []
      setLocations(Array.isArray(locationsData) ? locationsData : [])
      setUseLrLocationLabels(
        !!settingsRes?.data?.lr_branch_code_required
        || isLrShipmentFormat(settingsRes?.data?.shipment_id_format),
      )
      const routesData = routesRes?.data?.data || routesRes?.data || []
      setRoutes(Array.isArray(routesData) ? routesData : [])
    } catch (error) {
      console.error("Error fetching initial data:", error)
      Alert.error("Failed to load initial data")
    } finally {
      setLoading(false)
    }
  }

  const fetchExistingMLS = async () => {
    setExistingError("")
    setExistingLoading(true)
    try {
      const response = await mlSheetService.getAllMLSheets(1, 100, { all: true })
      const data = response.data?.data || []
      setExistingMLS(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error("Error loading existing ML sheets:", error)
      setExistingError(error.response?.data?.error || "Failed to load existing ML sheets")
    } finally {
      setExistingLoading(false)
    }
  }

  const handleCameraCapture = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      await handleFileSelect({ target: { files: [file] } }, { fromCamera: true })
    } catch (error) {
      console.error('Camera capture failed:', error)
      Alert.error('Could not process camera photo. Try again with the QR centered and well lit.')
    }

    if (cameraInputRef.current) {
      cameraInputRef.current.value = ""
    }
  }

  const importExistingMLSheet = (sheet) => {
    const fromIds = sheet.from_location_ids || []
    const toIds = sheet.to_location_ids || []
    const shipmentIds = sheet.shipment_ids || []

    setExtractedData((prev) => ({
      ...prev,
      fromLocationIds: applyLockedFrom([...new Set([...prev.fromLocationIds, ...fromIds])]),
      toLocationIds: [...new Set([...prev.toLocationIds, ...toIds])],
      shipmentIds: [...new Set([...prev.shipmentIds, ...shipmentIds])]
    }))

    setSelectedShipmentIds((prev) => [...new Set([...prev, ...shipmentIds])])

    const newShipments = shipmentIds
      .filter((id) => !extractedShipments.some((s) => s.shipment_id === id))
      .map((id) => ({ shipment_id: id }))

    if (newShipments.length) {
      setExtractedShipments((prev) => [...prev, ...newShipments])
    }

    Alert.success(`Imported ${shipmentIds.length} shipments from ML Sheet ${sheet.ml_sheet_id}`)
  }

  const handleFileSelect = async (e, options = {}) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    for (const rawFile of files) {
      if (!isAcceptedScanFile(rawFile)) {
        Alert.error(`Invalid file type: ${rawFile.name || 'capture'}. Only PDF and images are allowed.`)
        continue
      }

      let file = rawFile

      // Camera: fix EXIF + compress. Gallery upload: keep original so server QR decode stays sharp.
      if (isAcceptedImageFile(rawFile)) {
        try {
          file = await prepareScanImageFile(rawFile, { fromCamera: options.fromCamera })
        } catch (error) {
          console.error('Image normalization failed:', error)
          Alert.error(`Could not process ${rawFile.name || 'image'}. Try a clearer photo or upload from files.`)
          continue
        }
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        Alert.error(`File ${file.name} is too large. Maximum size is 10MB.`)
        continue
      }

      // Add to uploaded files
      const fileId = Date.now() + Math.random()
      const newFile = {
        id: fileId,
        name: file.name,
        type: file.type,
        size: file.size,
        file: file,
        preview: null,
        extracted: [],
        processing: false
      }

      // Generate preview for images
      if (file.type.startsWith("image/") || /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(file.name || "")) {
        const reader = new FileReader()
        reader.onload = (event) => {
          setUploadedFiles(prev =>
            prev.map(f => f.id === fileId ? { ...f, preview: event.target.result } : f)
          )
        }
        reader.readAsDataURL(file)
      }

      setUploadedFiles(prev => [...prev, newFile])

      // Process file immediately
      await processFile(file, fileId)
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const mergeScanResponse = (response, sourceLabel = 'scan') => {
    if (!response.data?.success || !response.data?.data) {
      throw new Error(response.data?.error || 'No shipments found in scan')
    }

    const shipments = response.data.data.shipments || []
    if (!shipments.length) {
      throw new Error('No eligible shipments found')
    }

    setExtractedShipments((prev) => {
      const existing = new Set(prev.map((s) => s.shipment_id))
      const added = shipments.filter((s) => !existing.has(s.shipment_id))
      return [...prev, ...added]
    })

    const newFromLocations = response.data.data.from_location_ids || []
    const newToLocations = response.data.data.to_location_ids || []
    const extractedIds = shipments.map((s) => s.shipment_id)

    setExtractedData((prev) => ({
      ...prev,
      fromLocationIds: applyLockedFrom([...new Set([...prev.fromLocationIds, ...newFromLocations])]),
      toLocationIds: [...new Set([...prev.toLocationIds, ...newToLocations])],
      shipmentIds: [...new Set([...prev.shipmentIds, ...extractedIds])],
    }))

    setSelectedShipmentIds((prev) => [...new Set([...prev, ...extractedIds])])
    Alert.success(`Added ${shipments.length} shipment(s) from ${sourceLabel}`)
  }

  const handleLiveQrScan = async ({ qrData, shipmentId, imageFile }) => {
    if (liveScanning) return
    try {
      setLiveScanning(true)
      try {
        const response = await mlSheetService.resolveQrScan({
          qrData,
          shipmentIds: shipmentId ? [shipmentId] : undefined,
        })
        mergeScanResponse(response, 'live QR scan')
      } catch (error) {
        if (!imageFile) throw error
        const formData = new FormData()
        formData.append('image', imageFile)
        const response = await mlSheetService.scanMLSheetImage(formData)
        mergeScanResponse(response, 'captured QR image')
      }
    } catch (error) {
      console.error('Live QR resolve failed:', error)
      Alert.error(error.response?.data?.error || error.message || 'Could not resolve scanned QR')
    } finally {
      setLiveScanning(false)
      setLiveScannerOpen(false)
    }
  }

  const processFile = async (file, fileId) => {
    setUploadedFiles(prev =>
      prev.map(f => f.id === fileId ? { ...f, processing: true } : f)
    )

    try {
      const formData = new FormData()
      formData.append("image", file)

      const response = await mlSheetService.scanMLSheetImage(formData)

      if (response.data?.success && response.data?.data) {
        const shipments = response.data.data.shipments || []
        setExtractedShipments(prev => [...prev, ...shipments])
        
        // Add location IDs to extracted data
        const newFromLocations = response.data.data.from_location_ids || []
        const newToLocations = response.data.data.to_location_ids || []
        
        setExtractedData(prev => ({
          ...prev,
          fromLocationIds: applyLockedFrom([...new Set([...prev.fromLocationIds, ...newFromLocations])]),
          toLocationIds: [...new Set([...prev.toLocationIds, ...newToLocations])],
          shipmentIds: [...new Set([...prev.shipmentIds, ...(response.data.data.shipments?.map(s => s.shipment_id) || [])])]
        }))

        // Auto-select extracted shipments
        const extractedIds = shipments.map(s => s.shipment_id)
        setSelectedShipmentIds(prev => [...new Set([...prev, ...extractedIds])])

        setUploadedFiles(prev =>
          prev.map(f => f.id === fileId ? { ...f, extracted: shipments } : f)
        )

        Alert.success(`Extracted ${shipments.length} shipment(s) from ${file.name}`)
      }
    } catch (error) {
      console.error("Error processing file:", error)
      Alert.error(`Failed to process ${file.name}: ${error.response?.data?.error || error.message}`)
    } finally {
      setUploadedFiles(prev =>
        prev.map(f => f.id === fileId ? { ...f, processing: false } : f)
      )
    }
  }

  const handleRemoveFile = (fileId) => {
    const fileToRemove = uploadedFiles.find(f => f.id === fileId)
    if (fileToRemove) {
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId))
      setExtractedShipments(prev => prev.filter(s => !fileToRemove.extracted.some(e => e.shipment_id === s.shipment_id)))
    }
  }

  const handleVehicleChange = (vehicleId) => {
    const selectedVehicle = vehicles.find(v => (v.id || v.vehicle_id) === vehicleId)
    setFormData({
      ...formData,
      vehicle_id: vehicleId,
      driver_contact: selectedVehicle?.driver_contact || "",
    })
    setOpenDropdown(null)
    setSearchInputs(prev => ({ ...prev, vehicle: "" }))
  }

  const handleRouteSelect = (routeId) => {
    if (!routeId) {
      setRouteLocked(false)
      setFormData((prev) => ({ ...prev, route_id: "" }))
      return
    }
    const route = routes.find((r) => String(r.id) === String(routeId))
    if (!route) return
    setRouteLocked(true)
    setFormData((prev) => ({ ...prev, route_id: route.id }))
  }

  const handleAccessUserChange = (userId) => {
    if (accessUsersRequired && userId === user?.user_id) {
      Alert.error('You cannot assign yourself as an ML access agent')
      return
    }
    setFormData(prev => ({
      ...prev,
      access_user_ids: prev.access_user_ids.includes(userId)
        ? prev.access_user_ids.filter(id => id !== userId)
        : [...prev.access_user_ids, userId],
    }))
  }

  const handleCrossingHubToggle = (locationId) => {
    setFormData((prev) => {
      const currentIds = prev.crossing_location_hub_ids || []
      const idStr = String(locationId)
      const alreadySelected = currentIds.some((id) => String(id) === idStr)
      return {
        ...prev,
        crossing_location_hub_ids: alreadySelected
          ? currentIds.filter((id) => String(id) !== idStr)
          : [...currentIds, locationId],
      }
    })
  }

  const isLocationSelected = (selectedIds, locationId) =>
    selectedIds.some((id) => String(id) === String(locationId))

  const getFilteredLocations = (searchTerm) =>
    filterLocationsByQuery(locations, searchTerm)

  const locationOptionLabel = (loc) =>
    formatLocationDropdownLabel(loc, useLrLocationLabels)
      || loc.location
      || loc.hub_name
      || loc.name
      || ''

  const handleSearchInput = (inputType, value) => {
    setSearchInputs(prev => ({
      ...prev,
      [inputType]: value
    }))
  }

  const getFilteredVehicles = (searchTerm) => {
    if (!searchTerm) return vehicles
    return vehicles.filter(vehicle => {
      const vehicleNumber = (vehicle.vehicle_number || "").toLowerCase()
      const vehicleType = (vehicle.vehicle_type || "").toLowerCase()
      const ownerName = (vehicle.owner_name || "").toLowerCase()
      const searchLower = searchTerm.toLowerCase()
      return (
        vehicleNumber.includes(searchLower) ||
        vehicleType.includes(searchLower) ||
        ownerName.includes(searchLower)
      )
    })
  }

  const getFilteredRoutes = (searchTerm) => {
    const q = (searchTerm || "").toLowerCase().trim()
    if (!q) return routes
    return routes.filter((route) => formatRouteLabel(route).toLowerCase().includes(q))
  }

  const renderAccessAgentOption = (agent) => {
    const agentId = agent.user_id || agent.id
    const meta = formatAccessAgentMeta(agent)
    return (
      <label
        key={agentId}
        className="flex items-start gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
      >
        <input
          type="checkbox"
          checked={formData.access_user_ids.includes(agentId)}
          onChange={() => handleAccessUserChange(agentId)}
          className="w-4 h-4 mt-0.5 flex-shrink-0"
        />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-secondary">{agent.name}</p>
          {meta && <p className="text-xs text-gray-500 mt-0.5">{meta}</p>}
        </div>
      </label>
    )
  }

  const accessAgentGroups = groupAccessAgents(
    agents,
    searchInputs.access,
    extractedData.toLocationIds,
    locations,
  )

  const locationAssignHubs = useMemo(
    () => ownHubsEligibleForMlLocationAssign(extractedData.toLocationIds, locations, agents),
    [extractedData.toLocationIds, locations, agents],
  )

  useEffect(() => {
    if (!locationAssignHubs.length) {
      setAssignToLocation(false)
      setAssignedLocationHubId("")
      return
    }
    setAssignToLocation(true)
    setAssignedLocationHubId((prev) => {
      const ids = locationAssignHubs.map((h) => String(hubIdOf(h)))
      if (prev && ids.includes(String(prev))) return prev
      return hubIdOf(locationAssignHubs[0])
    })
  }, [locationAssignHubs])

  useEffect(() => {
    if (assignToLocation) return
    const ids = matchingAccessAgentIds(
      agents,
      extractedData.toLocationIds,
      locations,
      user?.user_id,
    )
    setFormData((prev) => {
      const same =
        prev.access_user_ids.length === ids.length &&
        ids.every((id) => prev.access_user_ids.some((x) => String(x) === String(id)))
      if (same) return prev
      return { ...prev, access_user_ids: ids }
    })
  }, [assignToLocation, agents, extractedData.toLocationIds, locations, user?.user_id])

  const getLocationName = (locationId) => {
    if (!locationId) return "N/A"
    const location = locations.find(l => (l.id || l.hub_id) === locationId)
    return location ? (location.location || location.hub_name || location.name) : "N/A"
  }

  const handleVehicleCreated = (createdVehicle) => {
    const newVehicleId = createdVehicle.id || createdVehicle.vehicle_id
    setVehicles(prev => [...prev, createdVehicle])
    setFormData(prev => ({
      ...prev,
      vehicle_id: newVehicleId,
      driver_contact: createdVehicle.driver_contact || prev.driver_contact,
    }))
    setOpenDropdown(null)
    setSearchInputs(prev => ({ ...prev, vehicle: '' }))
  }

  const openVehicleModal = (prefillNumber = '') => {
    setPendingVehicleSearch(prefillNumber || searchInputs.vehicle)
    setShowVehicleModal(true)
    setOpenDropdown(null)
  }

  const openCamera = async () => {
    if (liveScanning) return
    if (supportsLiveQrScan()) {
      const allowed = await ensureCameraPermission()
      if (!allowed) {
        Alert.error('Camera permission denied. Enable camera access in app settings.')
        return
      }
      setLiveScannerOpen(true)
      return
    }
    cameraInputRef.current?.click()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (mlHardBlocked) {
      Alert.error(mlCreationBlock.reason || 'ML sheet creation is temporarily blocked')
      return
    }

    // Validation
    if (!uploadedFiles.length && !extractedData.shipmentIds.length) {
      Alert.error("Please upload/capture or import from existing ML sheet first")
      return
    }

    if (!extractedData.fromLocationIds.length) {
      Alert.error("No from location extracted from documents")
      return
    }

    if (!extractedData.toLocationIds.length) {
      Alert.error("No to location extracted from documents")
      return
    }

    if (!selectedShipmentIds.length) {
      Alert.error("No shipments extracted from documents")
      return
    }

    if (!formData.vehicle_id) {
      Alert.error("Please select a vehicle")
      return
    }

    const driverContact = String(formData.driver_contact || "").trim()
    if (driverContact && !/^\d{10}$/.test(driverContact)) {
      Alert.error("Driver contact must be a 10-digit mobile number")
      return
    }

    if (accessUsersRequired && !assignToLocation && formData.access_user_ids.length === 0) {
      Alert.error("Please select at least one access user")
      return
    }
    if (assignToLocation && !assignedLocationHubId) {
      Alert.error("Select the own hub to assign this ML sheet to")
      return
    }

    try {
      setLoading(true)

      const payload = {
        from_location_ids: applyLockedFrom(extractedData.fromLocationIds),
        to_location_ids: extractedData.toLocationIds,
        vehicle_id: formData.vehicle_id,
        driver_contact: formData.driver_contact,
        shipment_ids: selectedShipmentIds,
        access_user_ids: assignToLocation ? [] : formData.access_user_ids,
        assigned_location_hub_id: assignToLocation ? assignedLocationHubId : null,
        crossing_location_hub_ids: canSelectCrossingHubs ? formData.crossing_location_hub_ids : [],
        route_id: formData.route_id || null,
      }

      console.log("[v0] Creating ML Sheet from document scan with payload:", payload)

      const response = await mlSheetService.createMLSheet(payload)
      showMlSheetAutoSyncAlerts(response, { baseSuccess: 'ML Sheet created successfully!' })
      navigate("/ml-sheets")
    } catch (error) {
      console.error("Error creating ML Sheet:", error)
      Alert.error(error.response?.data?.error || "Failed to create ML Sheet")
    } finally {
      setLoading(false)
    }
  }

  const getVehicleName = (vehicleId) => {
    return vehicles.find(v => (v.id || v.vehicle_id) === vehicleId)?.vehicle_number || vehicleId
  }

  const getAgentName = (agentId) => {
    return agents.find(a => a.user_id === agentId || a.id === agentId)?.name || agentId
  }

  return (
    <DashboardLayout>
      {(loading && !agents.length) ? (
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message={t("common.loading")} />
        </div>
      ) : (
        <>
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" onClick={() => navigate("/ml-sheets")}>
                    <ArrowLeft size={20} className="text-gray-600" />
                  </Button>
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary">{t('pages.mlSheet.createQr')}</h1>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <MlPaidSettlementGate
                  mlCreationBlock={mlCreationBlock}
                  dues={dues}
                  agentDues={agentDues}
                  show={showPaidSettlementGate}
                  hardBlocked={mlHardBlocked}
                  settleOpen={settleOpen}
                  setSettleOpen={setSettleOpen}
                  onSettleSubmitted={refreshMlSettlement}
                  settlementPath={settlementPath}
                  allowSettle={!isHubAdmin}
                  className="mb-3"
                />
                {/* File Upload & ML sheet source tab */}
                <div className="bg-white rounded-xl shadow p-6">
                  <h2 className="text-lg font-semibold text-secondary mb-4">Step 1: Scan or Upload QR</h2>

                  <p className="text-gray-600 text-sm mb-4">
                    Open camera to auto-detect shipment QR codes, or upload PDF/images.
                  </p>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <Button
                      type="button"
                      variant="primary"
                      icon={Camera}
                      onClick={openCamera}
                      disabled={liveScanning}
                    >
                      {liveScanning ? 'Processing…' : 'Open Camera'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      icon={Upload}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload
                    </Button>
                  </div>

                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-orange-500 hover:bg-orange-50 transition cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault()
                      e.currentTarget.classList.add("border-orange-500", "bg-orange-50")
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove("border-orange-500", "bg-orange-50")
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      e.currentTarget.classList.remove("border-orange-500", "bg-orange-50")
                      const files = e.dataTransfer.files
                      const event = { target: { files } }
                      handleFileSelect(event)
                    }}
                  >
                    <Upload className="mx-auto mb-2 text-gray-400" size={32} />
                    <p className="font-medium text-gray-700">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-500 mt-1">PDF, PNG, JPG, GIF up to 10MB</p>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,image/png,image/jpeg,image/gif,.png,.jpg,.jpeg,.gif"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleCameraCapture}
                    className="hidden"
                  />

                  {uploadedFiles.length > 0 && (
                    <div className="mt-6 space-y-3">
                      <h3 className="font-medium text-gray-700">Uploaded Files:</h3>
                      {uploadedFiles.map(file => (
                        <div key={file.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                          <File size={20} className="text-gray-400" />
                          <div className="flex-1">
                            <p className="font-medium text-gray-700">{file.name}</p>
                            <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                            {file.extracted.length > 0 && (
                              <p className="text-xs text-green-600 font-medium mt-1">✓ {file.extracted.length} shipment(s) extracted</p>
                            )}
                          </div>
                          {file.processing && (
                            <div className="text-orange-500 text-sm">Processing...</div>
                          )}
                          {file.preview && (
                            <button
                              type="button"
                              onClick={() => setPreviewImage(file.preview)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl transition"
                            >
                              <Eye size={18} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(file.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Shipments Preview */}
                {extractedShipments.length > 0 && (
                  <div className="bg-white rounded-xl shadow p-6">
                    <h2 className="text-lg font-semibold text-secondary mb-4">Step 2: Review Extracted Shipments</h2>
                    
                    <div className="text-sm text-gray-700 mb-3">
                      <p><span className="font-semibold">Extracted From:</span> {extractedData.fromLocationIds.map(getLocationName).filter(Boolean).join(", ") || "N/A"}</p>
                      <p><span className="font-semibold">Extracted To:</span> {extractedData.toLocationIds.map(getLocationName).filter(Boolean).join(", ") || "N/A"}</p>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      <button
                        type="button"
                        onClick={() => setActionMode("create")}
                        className={`px-4 py-2 rounded-xl border ${actionMode === "create" ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-700 border-gray-300"}`}
                      >
                        Create New ML Sheet
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setActionMode("existing")
                          if (!existingMLS.length) fetchExistingMLS()
                        }}
                        className={`px-4 py-2 rounded-xl border ${actionMode === "existing" ? "bg-green-500 text-white border-green-500" : "bg-white text-gray-700 border-gray-300"}`}
                      >
                        Add to Existing ML Sheet
                      </button>
                    </div>

                    {actionMode === "existing" && (
                      <div className="mb-4 bg-gray-50 border border-gray-200 p-4 rounded-xl">
                        <p className="text-sm font-medium text-secondary mb-2">Choose target ML Sheet and update with selected shipments</p>
                        <div className="flex gap-2 items-center">
                          <SelectField variant="form"
                            value={selectedExistingSheet}
                            onChange={(e) => setSelectedExistingSheet(e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-xl"
                          >
                            <option value="">-- select existing ML Sheet --</option>
                            {existingMLS.map((sheet) => (
                              <option key={sheet.ml_sheet_id} value={sheet.ml_sheet_id}>
                                {sheet.ml_sheet_id} ({sheet.vehicle_number || "No vehicle"})
                              </option>
                            ))}
                          </SelectField>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!selectedExistingSheet) {
                                Alert.error("Please select an existing ML sheet")
                                return
                              }
                              if (!selectedShipmentIds.length) {
                                Alert.error("Please select at least one shipment")
                                return
                              }

                              setAddExistingLoading(true)
                              try {
                                const sheetResp = await mlSheetService.getMLSheet(selectedExistingSheet)
                                const existingShipmentIds = sheetResp.data?.ml_sheet?.shipment_ids || []
                                const mergedIds = [...new Set([...existingShipmentIds, ...selectedShipmentIds])]

                                const updateResp = await mlSheetService.updateMLSheet(selectedExistingSheet, { extracted_shipment_ids: selectedShipmentIds })
                                showMlSheetAutoSyncAlerts(updateResp, {
                                  baseSuccess: `Updated ML Sheet ${selectedExistingSheet} with ${selectedShipmentIds.length} shipments`,
                                })
                                navigate('/ml-sheets')
                              } catch (err) {
                                console.error("Error updating existing ML sheet:", err)
                                Alert.error(err.response?.data?.error || "Failed to update existing ML sheet")
                              } finally {
                                setAddExistingLoading(false)
                              }
                            }}
                            className="px-3 py-2 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition"
                            disabled={addExistingLoading}
                          >
                            {addExistingLoading ? "Updating..." : "Merge Selected Shipments"}
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {extractedShipments.map(shipment => (
                        <div key={shipment.shipment_id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedShipmentIds.includes(shipment.shipment_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedShipmentIds(prev => [...prev, shipment.shipment_id])
                                } else {
                                  setSelectedShipmentIds(prev => prev.filter(id => id !== shipment.shipment_id))
                                }
                              }}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-700">{shipment.shipment_id}</h3>
                              <div className="text-sm text-gray-600 mt-2 space-y-1">
                                <p><span className="font-medium">From:</span> {getLocationName(shipment.from_location_id) || shipment.sender_name || "N/A"}</p>
                                <p><span className="font-medium">To:</span> {getLocationName(shipment.to_location_id) || shipment.receiver_name || "N/A"}</p>
                              </div>
                            </div>
                            {selectedShipmentIds.includes(shipment.shipment_id) && (
                              <Check size={20} className="text-green-600 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Form Fields */}
                {extractedShipments.length > 0 && actionMode === "create" && (
                  <div className="bg-white rounded-xl shadow p-6 space-y-6">
                    <h2 className="text-lg font-semibold text-secondary">Step 3: Complete Details</h2>

                    {/* Extracted Locations Summary */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <p className="text-sm font-medium text-blue-900 mb-2">Locations Extracted:</p>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-blue-600">
                            From Locations ({extractedData.fromLocationIds.length})
                            {fromLocationLocked ? ' — locked to your hub' : ''}
                          </p>
                          <p className="text-blue-900 font-medium">{extractedData.fromLocationIds.map(getLocationName).join(", ") || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-blue-600">To Locations ({extractedData.toLocationIds.length})</p>
                          <p className="text-blue-900 font-medium">{extractedData.toLocationIds.map(getLocationName).join(", ") || "N/A"}</p>
                        </div>
                      </div>
                    </div>

                    <div className="relative min-w-0" ref={routeRef}>
                      <label className="block text-sm font-semibold text-secondary mb-2">
                        Route (optional)
                      </label>
                      <div
                        onClick={() =>
                          setOpenDropdown(openDropdown === "route" ? null : "route")
                        }
                        className="cursor-pointer border border-gray-300 rounded-xl px-3 py-2 bg-white flex justify-between items-center min-h-[42px]"
                      >
                        <span className="text-sm text-gray-700 truncate pr-2">
                          {formData.route_id
                            ? formatRouteLabel(routes.find((r) => String(r.id) === String(formData.route_id))) || "Custom — select locations manually"
                            : "Custom — select locations manually"}
                        </span>
                        <span
                          className={`text-gray-400 transition shrink-0 ${
                            openDropdown === "route" ? "rotate-180" : ""
                          }`}
                        >
                          ▾
                        </span>
                      </div>
                      {openDropdown === "route" && (
                        <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-gray-300 rounded-xl shadow p-2">
                          <input
                            type="text"
                            placeholder="Search routes..."
                            value={searchInputs.route}
                            onChange={(e) => handleSearchInput("route", e.target.value)}
                            autoFocus
                            className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                          />
                          <div className="max-h-48 overflow-y-auto">
                            <button
                              type="button"
                              onClick={() => {
                                handleRouteSelect("")
                                setOpenDropdown(null)
                                setSearchInputs((prev) => ({ ...prev, route: "" }))
                              }}
                              className={`w-full text-left p-2 hover:bg-gray-50 rounded ${
                                !formData.route_id ? "bg-blue-50" : ""
                              }`}
                            >
                              <span className={`text-sm ${!formData.route_id ? "text-blue-700 font-medium" : "text-gray-700"}`}>
                                Custom — keep scanned locations
                              </span>
                            </button>
                            {getFilteredRoutes(searchInputs.route).length > 0 ? (
                              getFilteredRoutes(searchInputs.route).map((route) => {
                                const isSelected = String(route.id) === String(formData.route_id)
                                return (
                                  <button
                                    key={route.id}
                                    type="button"
                                    onClick={() => {
                                      handleRouteSelect(route.id)
                                      setOpenDropdown(null)
                                      setSearchInputs((prev) => ({ ...prev, route: "" }))
                                    }}
                                    className={`w-full text-left p-2 hover:bg-gray-50 rounded ${
                                      isSelected ? "bg-blue-50" : ""
                                    }`}
                                  >
                                    <span className={`text-sm whitespace-normal break-words ${
                                      isSelected ? "text-blue-700 font-medium" : "text-gray-700"
                                    }`}>
                                      {formatRouteLabel(route)}
                                    </span>
                                  </button>
                                )
                              })
                            ) : (
                              <p className="text-sm text-gray-500 p-2">No matching routes</p>
                            )}
                          </div>
                        </div>
                      )}
                      {routeLocked ? (
                        <p className="text-xs text-blue-600 mt-1">
                          Route is locked. Choose Custom to auto-match from scanned locations again.
                        </p>
                      ) : (
                        formData.route_id && (
                          <p className="text-xs text-green-600 mt-1">
                            Matched route auto-selected for these locations.
                          </p>
                        )
                      )}
                    </div>

                    {canSelectCrossingHubs && (
                      <div className="relative min-w-0" ref={crossingRef}>
                        <label className="block text-sm font-semibold text-secondary mb-2">
                          Crossing Location Hubs
                        </label>
                        <div
                          onClick={() =>
                            setOpenDropdown(openDropdown === "crossing" ? null : "crossing")
                          }
                          className="cursor-pointer border border-gray-300 rounded-xl px-3 py-2 bg-white flex justify-between items-center min-h-[42px]"
                        >
                          <span className="text-sm text-gray-700 truncate pr-2">
                            {formData.crossing_location_hub_ids.length > 0
                              ? `${formData.crossing_location_hub_ids.length} selected`
                              : "Select location hubs"}
                          </span>
                          <span
                            className={`text-gray-400 transition shrink-0 ${
                              openDropdown === "crossing" ? "rotate-180" : ""
                            }`}
                          >
                            ▾
                          </span>
                        </div>
                        {openDropdown === "crossing" && (
                          <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-gray-300 rounded-xl shadow p-2">
                            <input
                              type="text"
                              placeholder="Search location hubs..."
                              value={searchInputs.crossing}
                              onChange={(e) => handleSearchInput("crossing", e.target.value)}
                              autoFocus
                              className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                            />
                            <div className="max-h-48 overflow-y-auto">
                              {getFilteredLocations(searchInputs.crossing).map((loc) => {
                                const locId = loc.id || loc.hub_id
                                return (
                                  <label
                                    key={locId}
                                    className="flex items-start gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isLocationSelected(formData.crossing_location_hub_ids, locId)}
                                      onChange={() => handleCrossingHubToggle(locId)}
                                      className="w-4 h-4 mt-0.5 shrink-0"
                                    />
                                    <span className="text-sm text-gray-700">
                                      {locationOptionLabel(loc)}
                                    </span>
                                  </label>
                                )
                              })}
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Agents at these hubs can create a crossing from QR and will see these shipments on the crossing page.
                        </p>
                      </div>
                    )}

                    {/* Vehicle, Driver & Access Users — one row on md+ */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                      <div className="relative min-w-0" ref={vehicleRef}>
                        <label className="block text-sm font-semibold text-secondary mb-2">
                          Vehicle <span className="text-red-500">*</span>
                        </label>
                        <div
                          onClick={() =>
                            setOpenDropdown(openDropdown === "vehicle" ? null : "vehicle")
                          }
                          className="cursor-pointer border border-gray-300 rounded-xl px-3 py-2 bg-white flex justify-between items-center min-h-[42px]"
                        >
                          <span className="text-sm text-gray-700 truncate pr-2">
                            {formData.vehicle_id
                              ? formatVehicleLabel(
                                  vehicles.find(v => (v.id || v.vehicle_id) === formData.vehicle_id)
                                ) || "Select a vehicle"
                              : "Select a vehicle"}
                          </span>
                          <span
                            className={`text-gray-400 transition shrink-0 ${
                              openDropdown === "vehicle" ? "rotate-180" : ""
                            }`}
                          >
                            ▾
                          </span>
                        </div>

                        {openDropdown === "vehicle" && (
                          <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-gray-300 rounded-xl shadow p-2">
                            <input
                              type="text"
                              placeholder="Search vehicles..."
                              value={searchInputs.vehicle}
                              onChange={(e) => handleSearchInput('vehicle', e.target.value)}
                              autoFocus
                              className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                            />
                            <div className="max-h-48 overflow-y-auto">
                              {getFilteredVehicles(searchInputs.vehicle).length > 0 ? (
                                getFilteredVehicles(searchInputs.vehicle).map(vehicle => (
                                  <div
                                    key={vehicle.id || vehicle.vehicle_id}
                                    onClick={() => handleVehicleChange(vehicle.id || vehicle.vehicle_id)}
                                    className="p-2 hover:bg-gray-50 rounded cursor-pointer flex items-center gap-2"
                                  >
                                    <input
                                      type="radio"
                                      checked={formData.vehicle_id === (vehicle.id || vehicle.vehicle_id)}
                                      onChange={() => {}}
                                      className="w-4 h-4"
                                    />
                                    <span className="text-sm font-medium text-gray-800">
                                      {formatVehicleLabel(vehicle)}
                                    </span>
                                  </div>
                                ))
                              ) : (
                                <p className="text-sm text-gray-500 p-2">No vehicles found</p>
                              )}
                            </div>
                            {canAddVehicle && (
                            <button
                              type="button"
                              onClick={() => openVehicleModal(searchInputs.vehicle)}
                              className="w-full flex items-center justify-center gap-2 px-3 py-2 mt-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 text-sm font-medium"
                            >
                              <Plus size={16} />
                              Add New Vehicle
                            </button>
                            )}
                          </div>
                        )}
                        <CreateVehicleWithOwnerModal
                          open={showVehicleModal}
                          onClose={() => setShowVehicleModal(false)}
                          onSuccess={handleVehicleCreated}
                          initialVehicleNumber={pendingVehicleSearch}
                          companyAccess={companyAccess}
                        />
                        {(() => {
                          const sel = vehicles.find(v => (v.id || v.vehicle_id) === formData.vehicle_id)
                          return sel?.owner_name ? (
                            <p className="text-xs text-gray-600 mt-1">
                              Vehicle owner: <span className="font-medium">{sel.owner_name}</span>
                            </p>
                          ) : null
                        })()}
                      </div>

                      <div className="min-w-0">
                        <label className="block text-sm font-semibold text-secondary mb-2">Driver Contact</label>
                        <input
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          value={formData.driver_contact}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            driver_contact: String(e.target.value || "").replace(/\D/g, "").slice(0, 10),
                          }))}
                          placeholder="10-digit mobile number"
                          className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 min-h-[42px]"
                        />
                      </div>

                      <div className="relative min-w-0" ref={accessRef}>
                        <MlAssignToLocationBlock
                          eligibleHubs={locationAssignHubs}
                          assignToLocation={assignToLocation}
                          setAssignToLocation={setAssignToLocation}
                          assignedLocationHubId={assignedLocationHubId}
                          setAssignedLocationHubId={setAssignedLocationHubId}
                        />
                        {!assignToLocation && (
                        <>
                        <label className="block text-sm font-semibold text-secondary mb-2">
                          ML Access Agents <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <div
                          onClick={() =>
                            setOpenDropdown(openDropdown === "access" ? null : "access")
                          }
                          className="cursor-pointer border border-gray-300 rounded-xl px-3 py-2 bg-white flex justify-between items-center min-h-[42px]"
                        >
                          <span className="text-sm text-gray-700 truncate pr-2">
                            {formData.access_user_ids.length > 0
                              ? `${formData.access_user_ids.length} selected`
                              : "Select users"}
                          </span>
                          <span
                            className={`text-gray-400 transition shrink-0 ${
                              openDropdown === "access" ? "rotate-180" : ""
                            }`}
                          >
                            ▾
                          </span>
                        </div>

                        {openDropdown === "access" && (
                          <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-gray-300 rounded-xl shadow p-2">
                            <input
                              type="text"
                              placeholder="Search by name, location, contact..."
                              value={searchInputs.access}
                              onChange={(e) => handleSearchInput('access', e.target.value)}
                              autoFocus
                              className="w-full px-3 py-2 mb-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                            />
                            <div className="max-h-56 overflow-y-auto">
                              {Array.isArray(agents) && accessAgentGroups.all.length > 0 ? (
                                <>
                                  {accessAgentGroups.hasToFilter && accessAgentGroups.matched.length > 0 && (
                                    <>
                                      <p className="text-[11px] font-semibold text-indigo-700 px-2 py-1 sticky top-0 bg-white">
                                        Agents at destination
                                      </p>
                                      {accessAgentGroups.matched.map(renderAccessAgentOption)}
                                    </>
                                  )}
                                  {accessAgentGroups.hasToFilter && accessAgentGroups.others.length > 0 && (
                                    <>
                                      <p className="text-[11px] font-semibold text-gray-500 px-2 py-1 mt-1 border-t border-gray-100 sticky top-0 bg-white">
                                        Other agents
                                      </p>
                                      {accessAgentGroups.others.map(renderAccessAgentOption)}
                                    </>
                                  )}
                                  {!accessAgentGroups.hasToFilter &&
                                    accessAgentGroups.all.map(renderAccessAgentOption)}
                                </>
                              ) : (
                                <p className="text-sm text-gray-500 p-2">
                                  {!agents.length
                                    ? 'No users available'
                                    : searchInputs.access.trim()
                                    ? 'No users match your search'
                                    : 'No users available'}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        <p className="text-xs text-gray-500 mt-1">
                          Selected: {formData.access_user_ids.length}
                        </p>
                        </>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                      <div className="flex gap-3 pt-6 border-t border-gray-200">
                        <Button variant="primary" type="submit" disabled={loading || !extractedShipments.length || mlHardBlocked} loading={loading} className="flex-1">
                          {loading ? "Creating..." : "Create ML Sheet"}
                        </Button>
                        <Button variant="secondary" type="button" onClick={() => navigate("/ml-sheets")} className="flex-1">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
              </form>

              {/* Image Preview Modal */}
              {previewImage && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                  onClick={() => setPreviewImage(null)}
                >
                  <div className="bg-white rounded-xl max-w-2xl w-full max-h-screen overflow-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-700">Document Preview</h3>
                      <button
                        onClick={() => setPreviewImage(null)}
                        className="p-1 hover:bg-gray-100 rounded-xl transition"
                      >
                        <X size={20} />
                      </button>
                    </div>
                    <img src={previewImage} alt="Document preview" className="w-full h-auto" />
                  </div>
                </div>
              )}

              <LiveQrScanner
                open={liveScannerOpen}
                onClose={() => setLiveScannerOpen(false)}
                onScan={handleLiveQrScan}
                busy={liveScanning}
                continuous={false}
                title="Scan Shipment QR"
                hint="Hold the label in the frame. The scanner closes after the QR is read."
              />
        </>
      )}
    </DashboardLayout>
  )
}

export default CreateMLSheetQR
